"use client";

import { HiMagnifyingGlass } from "react-icons/hi2";
import { cn } from "~/utils/fontsConfig";

import { GooglePlace } from "./GoogleMapsAutoComplete";
import { Input } from "./Input";
import { AnimatePresence, Variants } from "framer-motion";
import { motion } from "framer-motion";
import { useCallback, useRef, useState, useEffect } from "react";
import { ImSpinner8 } from "react-icons/im";

export interface InputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    prefixComponent?: React.ReactNode;
    theme?: "light" | "dark";
}

export interface EventsNearInputProps extends InputProps {
    onLocationChange: (place: GooglePlace) => void;
}

export const SearchInput = (props: InputProps) => {
    const { theme = "dark", ...rest } = props;
    return (
        <Input
            {...rest}
            className={cn(
                "rounded-full",
                theme === "light"
                    ? "bg-white text-black"
                    : "bg-v3-neutral text-white",
                props.className,
            )}
            prefixComponent={
                <HiMagnifyingGlass
                    className={cn(
                        "mr-1",
                        theme === "light" ? "text-black" : "text-white",
                    )}
                />
            }
        />
    );
};

const buttonVariants: Variants = {
    initial: { x: 0 },
    step1: { x: 0, borderRadius: "0.125rem" },
    step2: { marginLeft: 55, borderRadius: "9999px" },
};

const iconVariants: Variants = {
    hidden: { x: 50, opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: 50, opacity: 0, transition: { delay: 0 } },
};

const getResultItemVariants = (index: number, isUnsupported: boolean) => ({
    initial: {
        y: 0,
        scale: 0.3,
        filter: isUnsupported ? "none" : "blur(10px)",
    },
    animate: {
        y: (index + 1) * 50,
        scale: 1,
        filter: "blur(0px)",
    },
    exit: {
        y: isUnsupported ? 0 : -4,
        scale: 0.8,
        color: "#000000",
    },
});

const getResultItemTransition = (index: number) => ({
    duration: 0.75,
    delay: index * 0.12,
    type: "spring",
    bounce: 0.35,
    exit: { duration: index * 0.1 },
    filter: { ease: "easeInOut" },
});

export const GooeySearchInput = (
    props: InputProps & {
        isLoading: boolean;
        suffixComponent?: React.ReactNode;
        iconComponent?: React.ReactNode;
        theme?: "light" | "dark";
    },
) => {
    const { theme = "dark", ...rest } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentWidth, setContentWidth] = useState<number>(0);

    useEffect(() => {
        // Check if window width is mobile using a media query hook
        const isMobile =
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 1024px)").matches;
        if (isMobile) return;
        if (contentRef.current) {
            setContentWidth(contentRef.current.scrollWidth + 20); // Add some padding
        }
    }, []);

    const [state, setState] = useState({
        step: 1, // 1: Initial, 2: Search
        searchData: [],
        searchText: "",
        isLoading: false,
    });

    // Reset to initial state when props.value is cleared
    useEffect(() => {
        if (props.value && state.step === 1) {
            setState((prevState) => ({
                ...prevState,
                step: 2,
                searchText: props.value as string,
            }));
        }
    }, [props.value]);

    const handleButtonClick = () => {
        setState((prevState) => ({ ...prevState, step: 2 }));
    };

    const handleBlur = useCallback(
        (e: React.FocusEvent) => {
            if (state.searchText) return;

            // setTimeout to check focus after the current event cycle
            setTimeout(() => {
                // focus is within the input or suffix component
                const activeElement = document.activeElement;
                const inputHasFocus = inputRef.current?.contains(activeElement);
                const suffixHasFocus =
                    activeElement?.closest('[role="combobox"]') !== null;

                // Only collapse if neither has focus
                if (!inputHasFocus && !suffixHasFocus) {
                    setState((prevState) => ({ ...prevState, step: 1 }));
                }
            }, 0);
        },
        [state.searchText],
    );

    const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => ({
            ...prevState,
            searchText: e.target.value,
        }));
        props.onChange?.(e);
    };

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            isLoading: props.isLoading,
        }));
    }, [props.isLoading]);

    return (
        <div
            className={cn("relative flex w-full items-center", props.className)}
        >
            <GooeyFilter />
            <MotionBlurFiter />
            <div
                className={cn(
                    "w-full transition-[max-width] duration-300 ease-in-out",
                )}
                style={{
                    maxWidth:
                        contentWidth && state.step === 1
                            ? `${contentWidth}px`
                            : "100%",
                }}
            >
                <motion.div
                    ref={contentRef}
                    className="cursor-pointer"
                    initial="initial"
                    animate={state.step === 1 ? "step1" : "step2"}
                    transition={{
                        duration: 0.75,
                        type: "spring",
                        bounce: 0.15,
                    }}
                    style={{
                        filter: "url(#goo-effect)",
                    }}
                >
                    <AnimatePresence mode="wait">
                        {state.step === 2 && (
                            <motion.div
                                key="icon"
                                className={cn(
                                    "absolute top-[-1px]",
                                    "flex items-center justify-center",
                                    "rounded-full",
                                    theme === "light"
                                        ? "bg-white"
                                        : "bg-v3-neutral",
                                    "h-[46px] w-[46px]",
                                )}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                variants={iconVariants}
                                transition={{
                                    delay: 0.1,
                                    duration: 0.85,
                                    type: "spring",
                                    bounce: 0.15,
                                }}
                            >
                                {!state.isLoading ? (
                                    <SearchIcon theme={theme} />
                                ) : (
                                    <ImSpinner8
                                        className={cn(
                                            "h-5 w-5 animate-spin",
                                            theme === "light"
                                                ? "fill-black"
                                                : "fill-[#dddddd]",
                                        )}
                                    />
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.div
                        variants={buttonVariants}
                        onClick={handleButtonClick}
                        whileHover={{ scale: state.step === 2 ? 1 : 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={cn(
                            "cursor-pointer rounded-sm border-none outline-none",
                            theme === "light" ? "bg-white" : "bg-v3-neutral",
                            "flex h-11 items-center justify-center px-5 text-sm",
                            "h-[46px] w-inherit",
                        )}
                        role="button"
                    >
                        {state.step === 1 ? (
                            <div
                                className={cn(
                                    "pointer-events-none relative tracking-tighter",
                                    "flex flex-row",
                                    theme === "light"
                                        ? "text-v3-N-300"
                                        : "text-v3-N-400",
                                    "w-full",
                                )}
                            >
                                {props.iconComponent}
                                {props.placeholder}
                            </div>
                        ) : (
                            <>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    autoFocus
                                    className={cn(
                                        "w-full border-none bg-transparent outline-none",
                                        theme === "light"
                                            ? "text-black"
                                            : "text-[#dddddd]",
                                    )}
                                    placeholder="Type to search..."
                                    aria-label="Search input"
                                    value={state.searchText}
                                    onChange={handleSearch}
                                    onBlur={handleBlur}
                                />
                                {props.suffixComponent}
                            </>
                        )}
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export const InnerSearchIcon = () => {
    return (
        <motion.div
            className=""
            initial={{
                opacity: 0.5,
                scale: 0.8,
                x: -4,
                filter: "blur(2px)",
            }}
            animate={{
                opacity: 1,
                scale: 1,
                x: -4,
                filter: "blur(0px)",
            }}
            exit={{
                opacity: 0,
                scale: 0.8,
                x: -4,
                filter: "blur(5px)",
                transition: { delay: 0, duration: 0.2 },
            }}
            transition={{
                delay: 0.1,
                duration: 1,
                type: "spring",
                bounce: 0.15,
            }}
        >
            <HiMagnifyingGlass className="h-5 w-5 fill-[#dddddd]" />
        </motion.div>
    );
};

const SearchIcon = ({ theme = "dark" }: { theme?: "light" | "dark" }) => {
    return (
        <motion.div
            initial={{
                opacity: 0,
                scale: 0.8,
                x: -4,
                filter: "blur(5px)",
            }}
            animate={{
                opacity: 1,
                scale: 1,
                x: 0,
                filter: "blur(0px)",
            }}
            exit={{
                opacity: 0,
                scale: 0.8,
                x: -4,
                filter: "blur(5px)",
                transition: { delay: 0, duration: 0.2 },
            }}
            transition={{
                delay: 0.1,
                duration: 1,
                type: "spring",
                bounce: 0.15,
            }}
        >
            <HiMagnifyingGlass
                className={cn(
                    "h-5 w-5",
                    theme === "light" ? "fill-black" : "fill-[#dddddd]",
                )}
            />
        </motion.div>
    );
};

// https://tympanus.net/codrops/2024/11/18/how-to-create-a-gooey-search-interaction-with-framer-motion-and-react/
const GooeyFilter = () => {
    return (
        <svg aria-hidden="true" className="hidden">
            <defs>
                <filter id="goo-effect">
                    <feGaussianBlur
                        in="SourceGraphic"
                        stdDeviation="5"
                        result="blur"
                    />
                    <feColorMatrix
                        in="blur"
                        type="matrix"
                        values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -10"
                        result="goo"
                    />
                    <feComposite in="SourceGraphic" in2="goo" operator="atop" />
                </filter>
            </defs>
        </svg>
    );
};

const MotionBlurFiter = () => (
    <svg className="hidden">
        <defs>
            <filter id="motion-blur-effect">
                <feGaussianBlur in="SourceGraphic" stdDeviation="12,0" />
            </filter>
        </defs>
    </svg>
);
